import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import {
  compareAsc,
  differenceInCalendarDays,
  format,
  isSameDay,
  isSameYear,
  isValid,
  parseISO,
} from "date-fns";
import { FlightStatus } from "../../../enum/FlightsEnum";
import {
  ATM_CATEGORY_ABBREVIATIONS,
  BlockTimes,
  BlockTimeType,
  BlockTimeTypeSuffix,
  FLIGHTS_TABLE_LEG_TYPE_MAP,
} from "./../FlightConstants";

/**
 * Calculates the total delay duration from an array of string values representing numbers.
 *
 * @param value - An array of strings where each string represents a numeric value.
 * @returns A string representing the total delay duration in minutes (e.g., "15 mins"),
 *          or an empty string if the total is 0 or the input is invalid.
 */
export const getDelayDuration = (
  value: string[] | null | undefined
): string => {
  if (!Array.isArray(value) || value.length === 0) {
    return "";
  }

  const total = value
    .map((v) => Number.parseFloat(v))
    .filter((n) => Number.isFinite(n))
    .reduce((sum, n) => sum + n, 0);

  return total > 0 ? `${total} mins` : "";
};

/**
 * Converts an array of delay codes into a comma-separated string.
 *
 * @param value - An array of strings representing delay codes.
 * @returns A comma-separated string of delay codes if the array is not empty,
 *          an empty string if the array is empty or if the input is not an array.
 */
export const getDelayCodes = (value: string[] | null | undefined): string => {
  if (!Array.isArray(value) || value.length === 0) {
    return "";
  }
  return value.join(",");
};

/**
 * Retrieves the label for a flight leg based on the provided code.
 *
 * @param code - The code representing the flight leg type. If undefined, an empty string is returned.
 * @returns The corresponding label from `FLIGHTS_TABLE_LEG_TYPE_MAP` if the code exists,
 *          otherwise an empty string.
 */
export const getTableLegLabel = (code?: string) =>
  code ? (FLIGHTS_TABLE_LEG_TYPE_MAP[code] ?? "") : "";

/**
 * Abbreviates the flight security level for display in flight tables.
 */
export const getFlightSecurityLevelAbbreviation = (
  value: string | null | undefined
): string => {
  if (value === "Domestic") {
    return "DOM";
  }
  if (value === "International") {
    return "INT";
  }
  return value ?? "";
};

/**
 * Abbreviates an ATM category for display in flight tables.
 */
export const getAtmCategoryAbbreviation = (
  value: string | null | undefined
): string => (value ? (ATM_CATEGORY_ABBREVIATIONS[value] ?? value) : "");

/**
 * Determines if a specific column filter is active based on the provided column filters state.
 *
 * @param headerColumnId - The ID of the header column to check.
 * @param columnFilters - The current state of column filters, represented as an array of filter objects.
 * @returns `true` if the column filter with the specified ID is active, otherwise `false`.
 */
export const isColumnFilterActive = (
  headerColumnId: string,
  columnFilters: ColumnFiltersState
) => {
  if (columnFilters.length === 0) {
    return false;
  }
  return columnFilters.some((filter) => filter.id === headerColumnId);
};

/**
 * Determines if a specific column is currently active in the sorting state.
 *
 * @param headerColumnId - The ID of the header column to check.
 * @param columnSorts - The current sorting state, represented as an array of sorting objects.
 * @returns `true` if the specified column is active in the sorting state, otherwise `false`.
 */
export const isColumnSortActive = (
  headerColumnId: string,
  columnSorts: SortingState
) => {
  if (columnSorts.length === 0) {
    return false;
  }
  return columnSorts.some((sort) => sort.id === headerColumnId);
};

/**
 * Returns the CSS class name for a table row based on the flight status.
 *
 * @param status - The flight status as a string. It can be one of the values defined in `FlightStatus`,
 * or `undefined` if no status is provided.
 * @param styles - An object containing the CSS class mappings for each flight status.
 * The keys should correspond to the values in `FlightStatus`, and the values are the class names.
 *
 * @returns The CSS class name corresponding to the provided flight status. If the status is not
 * recognized or is `undefined`, an empty string is returned.
 */
export const getTableRowClass = (
  status: string | undefined,
  styles: any
): string => {
  const statusClassMap: Record<string, string> = {
    [FlightStatus.SCHEDULED]: styles.scheduledRow,
    [FlightStatus.ONTIME]: styles.onTimeRow,
    [FlightStatus.EARLY]: styles.onTimeRow,
    [FlightStatus.DELAYED]: styles.delayedRow,
    [FlightStatus.CANCELLED]: styles.cancelledRow,
  };

  const statusClass = status ? statusClassMap[status] || "" : "";

  return statusClass.trim();
};

export const getBlokTimeSuffix = (
  row: BlockTimes
): BlockTimeTypeSuffix | "" => {
  if (row.actualBlockTime) {
    return "ACT";
  }
  if (row.estimatedBlockTime) {
    return "EST";
  }
  if (row.scheduledBlockTime) {
    return "SCH";
  }
  return "";
};

/**
 * Rules:
 * - Today: "HH:mm SFX"             e.g., "09:21 EST"
 * - Yesterday/Tomorrow: "HH:mm±1 SFX" e.g., "16:36+1 SCH"
 * - Same year: "HH:mm MMM dd SFX"  e.g., "12:35 Sep 03 SCH"
 * - Other year: "HH:mm MMM dd, yyyy, SFX" e.g., "14:15 Dec 24, 2024, ACT"
 */
export const formatBlockTimeDisplay = (
  row: BlockTimes,
  blockTimeValue: string | undefined | null,
  currentTime: Date | undefined,
  blockTimeType?: BlockTimeType
): string => {
  if (!blockTimeValue) {
    return "—";
  }

  if (!currentTime) {
    return blockTimeValue;
  }

  // blockTimeValue is expected to be local date-time string and not in UTC
  const blockTime = parseISO(blockTimeValue);
  if (!isValid(blockTime)) {
    return "—";
  }

  const blockTimeTypeSuffix = blockTimeType
    ? blockTimeType.slice(0, 3)
    : getBlokTimeSuffix(row);
  const dayDiff = differenceInCalendarDays(blockTime, currentTime);

  if (isSameDay(blockTime, currentTime)) {
    return `${format(blockTime, "HH:mm")} ${blockTimeTypeSuffix}`;
  }
  if (dayDiff === 1) {
    return `${format(blockTime, "HH:mm")}+1 ${blockTimeTypeSuffix}`;
  }
  if (dayDiff === -1) {
    return `${format(blockTime, "HH:mm")}-1 ${blockTimeTypeSuffix}`;
  }
  if (isSameYear(blockTime, currentTime)) {
    return `${format(blockTime, "HH:mm MMM dd")} ${blockTimeTypeSuffix}`;
  }
  return `${format(blockTime, "HH:mm MMM dd, yyyy")} ${blockTimeTypeSuffix}`;
};

export const sortByBlockTime = (
  aValue: string | undefined | null,
  bValue: string | undefined | null
): number => {
  const a = aValue ? parseISO(aValue) : null;
  const b = bValue ? parseISO(bValue) : null;

  if (!a || !isValid(a)) {
    return !b || !isValid(b) ? 0 : 1;
  } // invalid goes last
  if (!b || !isValid(b)) {
    return -1;
  }
  return compareAsc(a, b);
};

/**
 * Returns a stable unique key for a flight row.
 */
export const getFlightRowUniqueKey = (row: any): string | null => {
  if (!row || typeof row !== "object") {
    return null;
  }

  // All flights view
  if (row.docId) {
    return String(row.docId);
  }

  // Turnaround view
  const arrivalDocId = row.arrival?.docId;
  const departureDocId = row.departure?.docId;
  if (arrivalDocId || departureDocId) {
    return `arr:${arrivalDocId ?? ""}|dep:${departureDocId ?? ""}`;
  }

  // Arrival or Departure view
  const arrivalFlightNumber = row.arrival?.flightNumber;
  const departureFlightNumber = row.departure?.flightNumber;
  const candidateKey =
    row.flightNumber ??
    (arrivalFlightNumber || departureFlightNumber
      ? `afn:${arrivalFlightNumber ?? ""}|dfn:${departureFlightNumber ?? ""}`
      : null);

  if (
    candidateKey === undefined ||
    candidateKey === null ||
    candidateKey === ""
  ) {
    return null;
  }

  return String(candidateKey);
};

/**
 * Returns the index of a flight row matching the provided unique key.
 * @param currentData - Array of flight data objects to search through
 * @param selectedRowKey - The unique key identifier to match
 * @returns The index of the matching row, or 0 if not found or key is null
 */
export const getSelectedRowIndexByUniqueKey = (
  currentData: any[],
  selectedRowKey: string | null
): number => {
  if (!selectedRowKey || currentData.length === 0) {
    return 0;
  }

  const newIndex = currentData.findIndex(
    (flight) => getFlightRowUniqueKey(flight) === selectedRowKey
  );

  return newIndex >= 0 ? newIndex : 0;
};
